import { DataRoomClientFile } from '@server/modules/client-matter/common/types';
import axios from 'axios';
import { saveAs } from 'file-saver';

import { renameGivenExistingNamesForDuplicates } from '@/common/utils';
import { getOSLimits, truncateMiddle } from '@/pages/overview/dataroom/content/common/utils';
import { showSuccessToast } from '@/utils/toasts/Toasts';
import { trpcClient } from '@/utils/trpc';

interface TempRedlineFile {
  name: string;
  url: string;
  extracted_text: string;
  average_file_embedding: number[];
}

export function renameDuplicateFilesForRedline(files: TempRedlineFile[]) {
  const usedFileNames = [] as string[];

  const newFiles = files.map((file) => {
    // TODO: there is probably a more efficient way to do this

    const fileName = file.name;
    const newName = renameGivenExistingNamesForDuplicates(fileName, usedFileNames);

    usedFileNames.push(newName);

    file.name = newName;

    return file;
  });
  return newFiles;
}

export async function createAndSelectRedlineFiles(
  redlineFilesUrls: DataRoomClientFile[],
  clientMatterNumber: number,
  clientNumber: number,
  clientMatterId: string,
  redlineName: string,
) {
  const s3FilesUrls = redlineFilesUrls.map((file) => {
    return file.name;
  });

  await trpcClient.redline.createRedline.mutate({
    name: redlineName,
    matterNumber: clientMatterNumber,
    clientNumber: clientNumber,
    clientMatterId: clientMatterId,
  });

  await trpcClient.redline.addRedlineFilesFromDataRoom.mutate({
    fileNames: s3FilesUrls,
    redlineIdentifier: {
      name: redlineName,
      clientNumber: clientNumber,
      matterNumber: clientMatterNumber,
      clientMatterId: clientMatterId,
    },
    clientMatterNumber: clientMatterNumber,
    clientNumber: clientNumber,
    clientMatterId: clientMatterId,
  });

  // Note: Extract Text With Formatting is now triggered during export generation
  // instead of during redline creation
}

export async function deleteRedline(
  clientMatterNumber: number,
  clientNumber: number,
  clientMatterId: string,
  redlineName: string,
) {
  await trpcClient.redline.deleteRedline.mutate({
    name: redlineName,
    matterNumber: clientMatterNumber,
    clientNumber: clientNumber,
    clientMatterId: clientMatterId,
  });
}

export async function changeTemplate({
  matterNumber,
  clusterName,
  clientNumber,
  clientMatterId,
  redlineName,
  newTemplateFile,
}: {
  matterNumber: number;
  clientNumber: number;
  clientMatterId: string;
  redlineName: string;
  clusterName: string;
  newTemplateFile: string;
}) {
  await trpcClient.redline.setNewTemplateForCluster.mutate({
    clusterIdentifier: {
      redlineIdentifier: {
        clientNumber,
        matterNumber,
        clientMatterId,
        name: redlineName,
      },
      clusterName,
    },
    clientMatterId,
    newTemplateFileId: newTemplateFile,
  });
}

/**
 * Initiates the export process for a redline comparison
 * @param baselineDocument The name of the baseline document
 * @param comparisonDocument The name of the document being compared
 * @param clientMatterId The ID of the client matter
 * @param clusterDiffId The ID of the cluster diff to export
 */
/**
 * Downloads a file from a URL using file-saver
 * @param url The URL to download from
 * @param filename The filename to save as
 * @param clientMatterId Optional client matter ID to get a presigned URL
 */
export async function downloadFromUrl(url: string, filename: string, clientMatterId?: string) {
  try {
    // If the URL is a direct S3 URL and we have a clientMatterId, get a presigned URL first
    if (url.includes('s3.') && clientMatterId) {
      const key = url.split('.com/')[1]; // Extract the S3 key from the URL

      // Get presigned URL using the trpc client
      const presignedResponse = await trpcClient.aws.getPresignedGets.query({
        clientMatterId,
        files: [
          {
            name: key,
            pdf: false, // This is already a PDF URL, no need to convert
          },
        ],
      });

      if (presignedResponse && presignedResponse.length > 0 && presignedResponse[0].url) {
        url = presignedResponse[0].url;
      }
    }

    const response = await axios.get(url, { responseType: 'blob' });
    saveAs(response.data, filename);
  } catch (error) {
    // Error downloading file - could add proper error handling here
  }
}

/**
 * Initiates the export process for a pairwise diff comparison
 * @param templateFileId The ID of the template file
 * @param comparingFileId The ID of the comparing file
 * @param clientMatterId The ID of the client matter
 */
export async function generatePairwiseDiffExport(
  templateFileId: string,
  comparingFileId: string,
  clientMatterId: string,
) {
  try {
    // Call the API to generate the pairwise diff export
    await trpcClient.redline.generatePairwiseDiffExport.mutate({
      clientMatterId,
      template_file_id: templateFileId,
      comparing_file_id: comparingFileId,
    });

    // Export generation initiated successfully
    showSuccessToast(
      'Redline export is being generated. This may take a few minutes. We will notify you when it is ready.',
      'w-[420px] h-[100px]',
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error generating pairwise diff export:', error);
    throw error; // Re-throw to allow caller to handle the error
  }
}

/**
 * Downloads a pairwise diff export directly using the pairwiseDiff ID
 * @param pairwiseDiffId The ID of the pairwise diff to download
 * @param baselineDisplayName The display name of the baseline document
 * @param comparingDisplayName The display name of the comparing document
 * @param format The format to download ('pdf' or 'docx'), defaults to 'pdf'
 */
export async function downloadPairwiseDiff(
  pairwiseDiffId: string,
  baselineDisplayName: string,
  comparingDisplayName: string,
  format: 'pdf' | 'docx' = 'pdf',
) {
  try {
    // Get OS-specific file name limits
    const { fileNameLimit } = getOSLimits();

    // Calculate how much space we have for each display name
    // We need to account for "Redline_", "_vs_", and the file extension
    const prefixLength = 'Redline_'.length;
    const middleLength = '_vs_'.length;
    const suffixLength = `.${format}`.length;
    const reservedLength = prefixLength + middleLength + suffixLength;

    // Calculate available space for the display names
    const availableSpace = fileNameLimit - reservedLength;

    // Allocate space proportionally based on the original lengths
    const totalOriginalLength = baselineDisplayName.length + comparingDisplayName.length;
    let baselineLength = Math.floor(
      (baselineDisplayName.length / totalOriginalLength) * availableSpace,
    );
    let comparingLength = Math.floor(
      (comparingDisplayName.length / totalOriginalLength) * availableSpace,
    );

    // Ensure minimum lengths and adjust if needed
    const minLength = 5;
    if (baselineLength < minLength) {
      baselineLength = minLength;
      comparingLength = availableSpace - minLength;
    } else if (comparingLength < minLength) {
      comparingLength = minLength;
      baselineLength = availableSpace - minLength;
    }

    // Truncate the display names if necessary
    const truncatedBaselineName = truncateMiddle(baselineDisplayName, baselineLength);
    const truncatedComparingName = truncateMiddle(comparingDisplayName, comparingLength);

    // Construct the filename from the truncated display names
    const filename = `Redline_${truncatedBaselineName}_vs_${truncatedComparingName}.${format}`;

    // Get presigned URL using the specialized endpoint for pairwise diffs
    const presignedResponse = await trpcClient.aws.getPairwiseDiffPresignedGets.query({
      pairwiseDiffIds: [pairwiseDiffId],
      format,
    });

    if (presignedResponse && presignedResponse.length > 0 && presignedResponse[0].url) {
      const url = presignedResponse[0].url;

      // Download the file using the presigned URL
      const response = await axios.get(url, { responseType: 'blob' });
      saveAs(response.data, filename);
    }
  } catch (error) {
    // Error downloading pairwise diff - could add proper error handling here
  }
}
